import clsx from 'clsx';
import { useFormik } from 'formik';
import React, { useEffect } from 'react';

import FormTitle from 'components/PageLayout/FormTitle';
import DateInput from 'components/curo-components/common/DateInput';
import Dropdown from 'components/curo-components/common/Dropdown';
import MonetaryInput from 'components/curo-components/common/MonetaryInput';
import RadioButton from 'components/curo-components/common/RadioButton';
import TextInput from 'components/curo-components/common/TextInput';
import Typography from 'components/curo-components/common/Typography';
import commonStyles from 'components/curo-components/forms/Forms.module.scss';
import { createFormikHelpers } from 'utils/curo-helpers/formHelpers';
import { LATIN_LETTERS_SPACE_DASH_REGEX, formatString } from 'utils/curo-helpers/validation';

import styles from './IncomeSourcesForm.module.scss';
import {
  EmploymentType,
  IIncomeSourcesFormValues,
  IncomeSourcesFields,
  PaycheckFrequency,
  incomeSourcesFormLabels,
} from './constants';
import { incomeInformationFormValidationSchema } from './validationSchema';

interface IIncomeSourceFormProps {
  handleSubmit: (values: IIncomeSourcesFormValues) => void;
  handleIsValidChanged: (
    isFormValid: boolean,
    submitFunction: () => Promise<IIncomeSourcesFormValues>,
  ) => void;
  handleRemove: () => void;
  initialData: IIncomeSourcesFormValues;
  isAdditional: boolean;
  setValues: (values: IIncomeSourcesFormValues) => void;
}

const PAGE_TITLE = 'Income Information';
const PAGE_SUBTITLE = (
  <>
    <Typography>Please provide the information requested below.</Typography>
    <br />
    <Typography>
      Alimony, child support, or separate maintenance income need not be revealed if you do not wish
      to have it considered as a basis for repaying this obligation.
    </Typography>
  </>
);

const IncomeSourcesForm = ({
  initialData,
  handleSubmit,
  handleIsValidChanged,
  isAdditional,
  setValues,
  handleRemove,
}: IIncomeSourceFormProps) => {
  const {
    handleSubmit: handleFormSubmit,
    touched,
    errors,
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    isValid,
    submitForm,
    setTouched,
  } = useFormik({
    initialValues: initialData,
    onSubmit: handleSubmit,
    validationSchema: incomeInformationFormValidationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  useEffect(() => {
    handleIsValidChanged(isValid, submitForm);
  }, [isValid]);

  useEffect(() => {
    setValues(values);
  }, [values]);

  const { handleInputChange, handleTouched, getInputErrorMessage } = createFormikHelpers<
    IncomeSourcesFields,
    Required<IIncomeSourcesFormValues>
  >(setFieldValue, setTouched, touched, errors);

  return (
    <>
      {isAdditional ? (
        <div className={styles.titleWrapper}>
          <h5 className={styles.additionalTitle}>Additional Source of Income</h5>
          <button onClick={handleRemove} className={styles.removeLink}>
            Remove
          </button>
        </div>
      ) : (
        <FormTitle title={PAGE_TITLE} subTitle={PAGE_SUBTITLE} />
      )}

      <form onSubmit={handleFormSubmit}>
        <section className={clsx(styles.formWrapper, commonStyles.formWrapper)}>
          <div className={styles.employmentTypeWrapper}>
            <RadioButton
              checked={values[IncomeSourcesFields.EmploymentType] === EmploymentType.Employed}
              labelText={EmploymentType.Employed}
              name={IncomeSourcesFields.EmploymentType}
              value={EmploymentType.Employed}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            <RadioButton
              checked={values[IncomeSourcesFields.EmploymentType] === EmploymentType.SelfEmployed}
              labelText={EmploymentType.SelfEmployed}
              name={IncomeSourcesFields.EmploymentType}
              value={EmploymentType.SelfEmployed}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            <RadioButton
              checked={values[IncomeSourcesFields.EmploymentType] === EmploymentType.Other}
              labelText={EmploymentType.Other}
              name={IncomeSourcesFields.EmploymentType}
              value={EmploymentType.Other}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
          </div>
          {values[IncomeSourcesFields.EmploymentType] === EmploymentType.Other && (
            <TextInput
              labelTitle={incomeSourcesFormLabels[IncomeSourcesFields.SourceOfIncome]}
              value={values[IncomeSourcesFields.SourceOfIncome]}
              onChange={handleInputChange(IncomeSourcesFields.SourceOfIncome)}
              onBlur={handleTouched(IncomeSourcesFields.SourceOfIncome)}
              errorMessage={getInputErrorMessage(IncomeSourcesFields.SourceOfIncome)}
              formatter={formatString(LATIN_LETTERS_SPACE_DASH_REGEX)}
              required
            />
          )}
          <MonetaryInput
            labelTitle={incomeSourcesFormLabels[IncomeSourcesFields.GrossMonthlyIncome]}
            value={values[IncomeSourcesFields.GrossMonthlyIncome]}
            onChange={handleInputChange(IncomeSourcesFields.GrossMonthlyIncome)}
            onBlur={handleTouched(IncomeSourcesFields.GrossMonthlyIncome)}
            errorMessage={getInputErrorMessage(IncomeSourcesFields.GrossMonthlyIncome)}
            titleHint="Gross monthly income is your income before taxes and deductions"
            tooltipClassName={styles.tooltipWrapper}
            required
          />
          <div className={styles.paycheckWrapper}>
            <DateInput
              name={IncomeSourcesFields.NextPayDate}
              labelTitle={incomeSourcesFormLabels[IncomeSourcesFields.NextPayDate]}
              value={values[IncomeSourcesFields.NextPayDate]}
              handleInputChange={handleInputChange(IncomeSourcesFields.NextPayDate)}
              onBlur={handleTouched(IncomeSourcesFields.NextPayDate)}
              errorMessage={getInputErrorMessage(IncomeSourcesFields.NextPayDate)}
              required
            />
            <Dropdown
              label={incomeSourcesFormLabels[IncomeSourcesFields.PaycheckFrequency]}
              value={values[IncomeSourcesFields.PaycheckFrequency] || ''}
              onSelect={handleInputChange(IncomeSourcesFields.PaycheckFrequency)}
              onBlur={handleTouched(IncomeSourcesFields.PaycheckFrequency)}
              errorMessage={getInputErrorMessage(IncomeSourcesFields.PaycheckFrequency)}
              options={Object.values(PaycheckFrequency)}
              required
            />
          </div>
        </section>
      </form>
    </>
  );
};

export default IncomeSourcesForm;
